<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faTrashAlt, faSave } from '@fortawesome/free-solid-svg-icons'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import AccountSelect from './accounts/account-select.svelte'
    import CategorySelect from './categories/category-select.svelte'
    import type { EntryTypeEnum } from '../../modules/financialApi/entries/entry.enum'
    import { Entry } from '../../modules/financialApi/entries/entry'

    const dispatch = createEventDispatcher()

    export let entryId: string = null
    export let creditDebit: EntryTypeEnum
    export let bank: string = null

    let show = false
    let entity: Entry = new Entry()
    let createdAt = dayjs(new Date()).format('YYYY-MM-DD')
    let paid: boolean = true

    let valuePending = false

    onMount(async () => {
        if (entryId) {
            await loadEntity(entryId)
        } else {
            newEntity()
        }
    })

    async function loadEntity(id: string) {
        try {
            show = true
            entity = new Entry()
            entity = await window.api.financials.entries.getOne(id)
            createdAt = dayjs(entity.paymentAt).format('YYYY-MM-DD')
            entity.person = await window.api.events.persons.getOne(entity.personId)
            paid = entity.status === 100
            console.log('entry', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        if (!creditDebit) {
            throw new Error('Tipo (credito/debito) não informado')
        }
        entity = new Entry()
        entity.createdAt = new Date(createdAt)
        entity.type = creditDebit
    }

    async function saveEntity(close: boolean) {
        upsertEntity(close)
    }

    async function upsertEntity(close: boolean) {
        try {
            if (show) return
            show = true
            if (!createdAt) {
                evaluateError('Informe data e hora de lançamento válidos')
                return false
            }
            entity.paymentAt = new Date(createdAt)
            // configure timezoneoffset
            entity.paymentAt.setMinutes(entity.paymentAt.getMinutes() + entity.paymentAt.getTimezoneOffset())
            entity.reference = entity.paymentAt
            const dto: Partial<Entry> = {
                entryId: entity.entryId || undefined,
                personId: entity.person?.personId,
                accountId: entity.account?.accountId,
                categoryId: entity.category?.categoryId,
                description: entity.description,
                paymentAt: entity.paymentAt,
                reference: entity.reference,
                source: 'manual',
                type: entity.type,
                value: entity.value,
                status: paid ? 100 : 95,
            }

            if (!validateDto(dto)) return false
            await window.api.financials.entries.upsert(dto)
            // só no lançamento novo: editar um existente não é "produzir" no módulo, é corrigir
            if (!entity.entryId) window.api.users.statistics.tick('financial.entry.create')
            if (close) {
                dispatch('change')
                return
            }
            window.alert('Salvo com sucesso.')
            newEntity()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateDto(dto) {
        if (!entity.entryId && !dto.personId) {
            evaluateError('Selecione a pessoa')
            return false
        }
        if (!dto.accountId) {
            evaluateError('Informe a conta')
            return false
        }
        if (!dto.categoryId) {
            evaluateError('Informe a categoria')
            return false
        }
        if (!dto.value) {
            evaluateError('Informe o valor')
            return false
        }
        return true
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.financials.entries.remove(entity.entryId)
            dispatch('change')
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    $: {
        if (!entity.entryId) {
            if (new Date(createdAt) <= new Date()) {
                paid = true
            } else {
                paid = false
            }
        }
    }
</script>

<div class="root m-bottom-sm">
    <div class="grid-act">
        <div class="spacer" />
        {#if window.store.token.permissions.financial.entries.write}
            {#if !!entity.entryId}
                <button class="btn btn-warning" on:click={removeEntity}><Fa icon={faTrashAlt} /> Excluir</button>
            {/if}
            {#if !entryId}
                <button class="btn btn-primary" on:click={() => saveEntity(false)}><Fa icon={faSave} /> Salvar</button>
            {/if}
            <button class="btn btn-primary" on:click={() => saveEntity(true)}>
                <Fa icon={faSave} /> Salvar e fechar
            </button>
        {/if}
    </div>
    <div class="grid-form">
        <div class="row">
            <div class="col-md-6">
                <AccountSelect label="Conta" bind:account={entity.account} {bank} />
            </div>
            <div class="col-md-6">
                <ZentitySelect fonte="pessoa" label="Pessoa / Empresa" bind:value={entity.person} />
            </div>
            <div class="col-md-6">
                <div class="input-group">
                    <label for="" class="input-group-text">Vencimento</label>
                    <input type="date" bind:value={createdAt} class="form-control" />
                </div>
            </div>
            <div class="col-md-6">
                <CategorySelect {creditDebit} label="Categoria" bind:category={entity.category} />
            </div>

            <div class="col-md-6">
                <div class="input-group">
                    <label for="" class="input-group-text">Descrição</label>
                    <input type="text" bind:value={entity.description} class="form-control" />
                </div>
            </div>
            <div class="col-md-6">
                <div class="input-group">
                    <label for="" class="input-group-text">Valor</label>
                    <input type="number" bind:value={entity.value} class="form-control" />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <div class="form-check form-switch">
                    <input class="form-check-input" bind:checked={paid} type="checkbox" role="switch" id="paid" />
                    <label class="form-check-label" for="paid">Esse valor está pago</label>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .row {
        padding: 0px 16px;
    }

    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column-reverse;
        }
        .grid-act {
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .grid-act .btn {
            max-width: 120px;
            flex-grow: 1;
        }
    }
</style>
